import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import ProductTile, { ProductGrid } from './ProductTile';
import { groupsFromFacets, groupOfType } from '../lib/productTypes';

// WO-10: sealed products of a set (setId) or matching a search (query). Renders nothing until there is at least
// one product, so set and search pages look exactly as before where no sealed product exists.
// WO-20: product tiles with type badges; `items` skips the fetch (the page already has them); `compact` shows one
// row with a "see all" link; `grouped` splits a set's products by kind (Booster Boxes, Elite Trainer Boxes, ...).
// WO-26: `showGame` adds the game badge to each tile (all-games search).
const SealedProductsSection = ({
  setId = null, query = null, title = 'Sealed products', limit = 60, items: given = null,
  compact = false, grouped = false, seeAllTo = null, seeAllLabel = 'See all', total = null, game = null, flush = false,
  showGame = false,
}) => {
  const apiUrl = process.env.REACT_APP_API_URL || '';
  const [fetched, setFetched] = useState([]);

  useEffect(() => {
    if (given) return undefined;
    if (!setId && !(query && query.trim().length >= 2)) { setFetched([]); return undefined; }
    const ctrl = new AbortController();
    const tcg = game && game !== 'pokemon' ? `&tcg=${encodeURIComponent(game)}` : '';
    const url = setId
      ? `${apiUrl}/api/products?set_id=${encodeURIComponent(setId)}&limit=${limit}`
      : `${apiUrl}/api/search/products?query=${encodeURIComponent(query)}&limit=${limit}${tcg}`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : { results: [] }))
      .then((data) => setFetched(Array.isArray(data?.results) ? data.results : []))
      .catch(() => setFetched([]));
    return () => ctrl.abort();
  }, [apiUrl, setId, query, limit, given, game]);

  const items = given || fetched;
  if (!items.length) return null;

  const heading = (
    <div className="flex items-end justify-between gap-3 mb-4">
      <h2 className="text-xl tracking-wider uppercase font-medium text-purple">
        {title}
        {total ? <span className="ml-2 text-sm text-cream/50 normal-case tracking-normal">({total})</span> : null}
      </h2>
      {seeAllTo && (
        <Link to={seeAllTo} className="text-sm text-skyblue hover:text-cream inline-flex items-center whitespace-nowrap">
          {seeAllLabel} <ChevronRight size={16} />
        </Link>
      )}
    </div>
  );

  if (compact) {   // one row at every width (scrolls sideways), never wraps into a second row
    return (
      <section className="mb-2" data-testid="sealed-products">
        {heading}
        <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
          {items.map((p) => (
            <div key={p.product_id} className="w-40 sm:w-44 shrink-0 snap-start"><ProductTile product={p} showSet={!setId} showGame={showGame} compact /></div>
          ))}
        </div>
      </section>
    );
  }

  if (grouped) {
    const groups = groupsFromFacets(items.map((p) => ({ product_type: p.product_type, type_name: p.type_name, count: 1 })));
    return (
      <section className="mt-2" data-testid="sealed-products">
        {groups.map((g) => (
          <div key={g.key} className="mb-8">
            <h3 className="text-sm uppercase tracking-widest text-cream/60 mb-3">{g.label} <span className="text-cream/40">({g.count})</span></h3>
            <ProductGrid items={items.filter((p) => groupOfType(p.product_type, p.type_name).key === g.key)} showSet={!setId} />
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className={flush ? 'mb-4' : 'mt-10'} data-testid="sealed-products">
      {heading}
      <ProductGrid items={items} showSet={!setId} showGame={showGame} />
    </section>
  );
};

export default SealedProductsSection;
